import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import VerifiedBadge from '../components/VerifiedBadge';
import api from '../api';

/**
 * Pulse — global activity ticker.
 * Reference: /app/reference/12-live-pulse.html
 *
 * Route: /pulse
 * Auth-required. Polls stats every 30s and feed every 8s.
 * Filters: all | buys | sells | following (clans the user holds ≥1 Bit in).
 */

const RELATIVE = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 15) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  return `${dd}d ago`;
};

const COUNTDOWN = (iso) => {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'live now';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d >= 1) return `${d}d ${h}h`;
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'buys', label: 'Buys' },
  { key: 'sells', label: 'Sells' },
  { key: 'following', label: 'Following' },
];

function StatMini({ value, label, testid }) {
  return (
    <div className="pulse-stat" data-testid={testid}>
      <div className="pulse-stat-val">{value}</div>
      <div className="pulse-stat-lbl">{label}</div>
    </div>
  );
}

function TradeRow({ trade, isFresh }) {
  const navigate = useNavigate();
  const { creator, side, qty, subtotal, trader_short, created_at, clan_handle } = trade;
  const isBuy = side === 'buy';
  const grad = creator?.gradient || ['#00B4FF', '#E040FB'];
  return (
    <button
      className={`pulse-row${isFresh ? ' fresh' : ''}`}
      onClick={() => navigate(`/creator/${clan_handle}`)}
      data-testid={`pulse-row-${trade.trade_id}`}
      type="button"
    >
      <div
        className="pulse-avatar"
        style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
      >
        {creator?.initials || '??'}
      </div>
      <div className="pulse-info">
        <div className="pulse-text">
          <b>{trader_short}</b>
          {' '}
          <span className={`pulse-verb ${isBuy ? 'buy' : 'sell'}`}>{isBuy ? 'bought' : 'sold'}</span>
          {' '}{qty} Bit{qty === 1 ? '' : 's'} of{' '}
          <b>@{creator?.handle || clan_handle}</b>
          {creator?.verified && <VerifiedBadge size="sm" className="pulse-verified" />}
        </div>
        <div className="pulse-time">{RELATIVE(created_at)}</div>
      </div>
      <div className="pulse-right">
        <div className="pulse-amount">{subtotal.toFixed(2)} pts</div>
        <div className={`pulse-icon ${isBuy ? 'buy' : 'sell'}`}>
          {isBuy ? (
            <svg viewBox="0 0 16 16" fill="none" width="10" height="10">
              <path d="M8 12V4M4 8l4-4 4 4" stroke="#B4FF00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="none" width="10" height="10">
              <path d="M8 4v8M4 8l4 4 4-4" stroke="#FF5722" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>
    </button>
  );
}

export default function Pulse() {
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);
  const [freshIds, setFreshIds] = useState(new Set());
  const [nextLaunch, setNextLaunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const seenIdsRef = useRef(new Set());

  const loadStats = useCallback(() => {
    api.get('/pulse/stats')
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  const loadFeed = useCallback((f) => {
    api.get(`/pulse/feed?filter=${f}&limit=30`)
      .then(({ data }) => {
        const list = data.trades || [];
        // Mark trades we've never seen as fresh (except on first load).
        const prevSeen = seenIdsRef.current;
        const isFirstLoad = prevSeen.size === 0;
        const newlyFresh = new Set();
        if (!isFirstLoad) {
          list.forEach((t) => {
            if (!prevSeen.has(t.trade_id)) newlyFresh.add(t.trade_id);
          });
        }
        seenIdsRef.current = new Set(list.map((t) => t.trade_id));
        setTrades(list);
        if (newlyFresh.size > 0) {
          setFreshIds(newlyFresh);
          // Clear fresh markers after animation window
          setTimeout(() => setFreshIds(new Set()), 1400);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const loadNextLaunch = useCallback(() => {
    api.get('/pulse/next-launch')
      .then(({ data }) => setNextLaunch(data.launch))
      .catch(() => setNextLaunch(null));
  }, []);

  // Reset seen-set when filter changes so we don't over-flash after switching
  useEffect(() => {
    seenIdsRef.current = new Set();
    setLoading(true);
    loadFeed(filter);
  }, [filter, loadFeed]);

  useEffect(() => {
    loadStats();
    loadNextLaunch();
    const t1 = setInterval(loadStats, 30000);
    const t2 = setInterval(() => loadFeed(filter), 8000);
    const t3 = setInterval(loadNextLaunch, 60000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); };
    // filter is intentionally captured via closure — refresh happens in the effect above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStats, loadFeed, loadNextLaunch, filter]);

  const fmtVolume = (v) => {
    if (v == null) return '—';
    if (v >= 10000) return `${(v / 1000).toFixed(1)}K pts`;
    return `${v.toFixed(2)} pts`;
  };
  const fmtInt = (n) => (n == null ? '—' : n.toLocaleString());

  return (
    <AppShell activeTab="pulse">
      <div className="pulse-wrap" data-testid="pulse-page">
        <div className="pulse-title-row">
          <span className="pulse-title" data-testid="pulse-title">
            <span className="pulse-live-dot" />
            Pulse
          </span>
          <div className="pulse-title-sub">Live activity across every Clan</div>
        </div>

        <div className="pulse-stats-strip" data-testid="pulse-stats">
          <StatMini
            value={fmtVolume(stats?.volume_today)}
            label="Volume · 24h"
            testid="pulse-stat-volume"
          />
          <StatMini
            value={fmtInt(stats?.trades_today)}
            label="Trades · 24h"
            testid="pulse-stat-trades"
          />
          <StatMini
            value={fmtInt(stats?.active_traders)}
            label="Active traders"
            testid="pulse-stat-active"
          />
        </div>

        {nextLaunch && (
          <Link
            to={`/launch/${nextLaunch.id}`}
            className="pulse-launch-banner"
            data-testid="pulse-launch-banner"
          >
            <div className="pulse-launch-icon">
              <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                <path d="M12 2c1 3-2 4-2 7a4 4 0 008 0c0-1-.5-2-1-2 1 4-1 6-3 6a3 3 0 01-3-3c0-2 1.5-3 1-8z" fill="#1A0E05" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pulse-launch-title">
                🚀 {nextLaunch.creator_name} launches in {COUNTDOWN(nextLaunch.starts_at)}
              </div>
              <div className="pulse-launch-sub">See all upcoming Clan launches</div>
            </div>
            <svg viewBox="0 0 16 16" fill="none" width="15" height="15" style={{ opacity: 0.7 }}>
              <path d="M6 3l5 5-5 5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        )}

        <div className="pulse-filters" data-testid="pulse-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`pulse-fchip pulse-fchip-${f.key}${filter === f.key ? ' selected' : ''}`}
              onClick={() => setFilter(f.key)}
              data-testid={`pulse-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="pulse-loading" data-testid="pulse-loading">Loading Pulse…</div>
        ) : trades.length === 0 ? (
          <div className="pulse-empty" data-testid="pulse-empty">
            <div className="pulse-empty-emoji">📡</div>
            <div className="pulse-empty-title">
              {filter === 'following' ? 'No trades on Clans you follow yet' : 'No trades yet'}
            </div>
            <p className="pulse-empty-sub">
              {filter === 'following'
                ? 'Buy Bits in a Clan to see its trades here.'
                : 'When traders start moving, you\'ll see them here in real time.'}
            </p>
          </div>
        ) : (
          <div className="pulse-feed" data-testid="pulse-feed">
            {trades.map((t) => (
              <TradeRow key={t.trade_id} trade={t} isFresh={freshIds.has(t.trade_id)} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
